import React, { useState, useEffect, useCallback } from 'react';
import { ResponsiveContainer, PieChart, Pie, Tooltip, Legend, Cell } from 'recharts';
import adminService from '../../../services/adminService';
import EmptyState from './EmptyState';
import { COLORS } from '../constants';

const CHART_COLORS = [COLORS.primary, COLORS.violet, COLORS.emerald, COLORS.blue, '#dc2626', '#ca8a04'];

export default function LocationInsightsSection({ topAffiliates, recentLinks }) {
  const [locationData, setLocationData] = useState({
    byCountry: [],
    totalClicks: 0,
    totalUnique: 0,
    filters: {},
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterUserId, setFilterUserId] = useState('');
  const [filterShortCode, setFilterShortCode] = useState('');

  const fetchLocationStats = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (filterUserId) params.userId = filterUserId;
      if (filterShortCode) params.shortCode = filterShortCode;
      const data = await adminService.getAffiliateLocationStats(params);
      setLocationData({
        byCountry: data.byCountry || [],
        totalClicks: data.totalClicks ?? 0,
        totalUnique: data.totalUnique ?? 0,
        filters: data.filters || {},
      });
    } catch (err) {
      setError(err?.message || 'Failed to load location stats');
      setLocationData({ byCountry: [], totalClicks: 0, totalUnique: 0, filters: {} });
    } finally {
      setLoading(false);
    }
  }, [filterUserId, filterShortCode]);

  useEffect(() => {
    fetchLocationStats();
  }, [fetchLocationStats]);

  const { byCountry, totalClicks, totalUnique } = locationData;
  const hasFilters = filterUserId || filterShortCode;

  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-slate-200/80 bg-white p-5 shadow-sm">
        <div className="mb-4 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h3 className="text-base font-semibold text-slate-900">Clicks by location</h3>
            <p className="mt-0.5 text-sm text-slate-500">
              Where your link clicks come from
              {hasFilters && ' (filtered)'}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <label className="flex items-center gap-2 text-sm text-slate-600">
              <span>User:</span>
              <select
                value={filterUserId}
                onChange={(e) => setFilterUserId(e.target.value)}
                className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-800 focus:border-orange-400 focus:outline-none focus:ring-1 focus:ring-orange-400"
              >
                <option value="">All users</option>
                {topAffiliates.map((a) => (
                  <option key={a.userId?.toString()} value={a.userId?.toString()}>
                    {a.name || a.email || a.userId}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-600">
              <span>Link:</span>
              <select
                value={filterShortCode}
                onChange={(e) => setFilterShortCode(e.target.value)}
                className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-800 focus:border-orange-400 focus:outline-none focus:ring-1 focus:ring-orange-400"
              >
                <option value="">All links</option>
                {recentLinks.map((link) => (
                  <option key={link._id} value={link.shortCode}>
                    {link.shortCode} – {link.visits ?? 0} visits
                  </option>
                ))}
              </select>
            </label>
          </div>
        </div>

        {error && (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
            {error}
          </div>
        )}

        {loading ? (
          <div className="flex h-80 items-center justify-center text-slate-500">Loading…</div>
        ) : byCountry.length === 0 ? (
          <EmptyState
            message="No location data yet"
            subMessage="Clicks with resolved IP location will appear here. New clicks are geolocated automatically."
          />
        ) : (
          <>
            <div className="mb-4 flex gap-4 text-sm">
              <span className="font-medium text-slate-700">
                Total clicks: <span className="text-slate-900">{totalClicks.toLocaleString()}</span>
              </span>
              <span className="font-medium text-slate-700">
                Unique: <span className="text-slate-900">{totalUnique.toLocaleString()}</span>
              </span>
            </div>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={byCountry}
                    dataKey="clicks"
                    nameKey="label"
                    cx="50%"
                    cy="50%"
                    outerRadius={120}
                    fill="#8884d8"
                    label={({ label, clicks, percent }) => `${label}: ${clicks} (${(percent * 100).toFixed(0)}%)`}
                  >
                    {byCountry.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{ borderRadius: 8 }}
                    formatter={(value, name, props) => [`${value} clicks`, props.payload.label]}
                    labelFormatter={(label) => `Location: ${label}`}
                  />
                  <Legend formatter={(value, entry) => entry.payload.label} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-4 overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 text-left text-xs font-medium uppercase text-slate-500">
                    <th className="py-2 pr-4">Location</th>
                    <th className="py-2 text-right">Clicks</th>
                    <th className="py-2 text-right">Unique</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {byCountry.map((row, i) => (
                    <tr key={i} className="hover:bg-slate-50/50">
                      <td className="py-2 pr-4 text-slate-700">{row.label || 'Unknown'}</td>
                      <td className="py-2 text-right tabular-nums text-slate-900">{row.clicks}</td>
                      <td className="py-2 text-right tabular-nums text-slate-600">{row.uniqueClicks}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
